import React from 'react';
import moment from 'moment';
import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';
import { groupBy } from 'lodash';
import { VENDOR_TYPES } from 'Details/utils';
import { HighlightedText } from './SearchDialog.styles';
import { getYearByDate } from 'utils/date';
import { SPEND_TYPE_LABELS } from '../utils';

export const COMPANY_FILTER_KEY = 'company';
export const dateFormatter = dateStr => moment(dateStr).format('MMM Do, YYYY');

export const colFormatters = {
	maxDate: dateFormatter,
	minDate: dateFormatter,
	binnedDate: dateFormatter,
	spend: (value, currencySymbol) =>
		`${currencySymbol}${getFormattedCurrencyWithIdealDenomination({
			value,
			isUnitSign: true,
			hideDecimals: false,
		})}`,
};

export const SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY =
	'search-selected-dimensions';

export const getHighlightedName = (name, searchText) => {
	if (!searchText) {
		return name;
	}
	const index = name.toLowerCase().indexOf(searchText.toLowerCase());
	const before = name.slice(0, index);
	const searchValue = name.slice(index, index + searchText.length);
	const after = name.slice(index + searchText.length, name.length);

	return (
		<React.Fragment>
			{before}
			<HighlightedText>{searchValue}</HighlightedText>
			{after}
		</React.Fragment>
	);
};

export const getHierarchy = row => {
	if (!row.dimensionValues) {
		return '';
	}

	const nullLabel = 'Null';
	const hierarchy = Object.values(row.dimensionValues).map(
		item => item || nullLabel
	);

	if (hierarchy.length === 1) {
		return '';
	}

	return hierarchy.join('/');
};

export const getColumnValue = ({ column, row, currencySymbol, search }) => {
	if (column.colName === 'period') {
		return `${dateFormatter(row.minDate)}–${dateFormatter(row.maxDate)}`;
	} else if (column.colName === 'value') {
		return getHighlightedName(row[column.colName], search);
	}
	return colFormatters[column.colName]
		? colFormatters[column.colName](row[column.colName], currencySymbol)
		: row[column.colName];
};

export const getCols = (transactions, selectedDimension) => {
	if (!transactions || !transactions.headers || !selectedDimension) {
		return [];
	}
	const groupedColumns = groupBy(transactions.headers, 'colName');
	return [
		{
			align: 'left',
			title: `${transactions.totalCount} items found`,
			colName: 'value',
			sortIndex: groupedColumns.value[0].sortIndex,
			width: 256,
		},
		{
			align: 'right',
			title: 'Spend',
			colName: 'spend',
			sortIndex: groupedColumns.spend[0].sortIndex,
			width: 92,
		},
		{
			align: 'left',
			title: 'Time period',
			colName: 'period',
			sortIndex: groupedColumns.minDate[0].sortIndex,
			width: 188,
		},
		{
			align: 'right',
			title: '# of transactions',
			colName: 'transactionsCount',
			sortIndex: groupedColumns.transactionsCount[0].sortIndex,
			width: 132,
		},
	];
};

export const QUICK_FILTERS_MAP = {
	0: 0,
	'Empty / Null': 1,
	'Contains numerical': 2,
	'Numerical only': 3,
	'Special characters': 4,
};

export const getCustomFilters = props => {
	const { storedQueryId, storedQuery } = props;
	let { customFiltersValues, customFilters } = props;

	if (storedQueryId) {
		if (storedQuery) {
			({ customFiltersValues, customFilters } = storedQuery);
		} else {
			customFiltersValues = [];
			customFilters = [];
		}
	}

	return { customFiltersValues, customFilters };
};

export const getWaterfallFilters = props => {
	const { supplierId, waterfallFilter, isHarmonized } = props;
	const vendorFilterName = !isHarmonized
		? VENDOR_TYPES.original
		: VENDOR_TYPES.harmonized;

	if (
		!waterfallFilter ||
		(supplierId && vendorFilterName === waterfallFilter[0])
	) {
		return [];
	}

	return [waterfallFilter[2]];
};

export const getTags = props => {
	const {
		showNonSourceable,
		useNetSpend,
		showNegativeSpend,
		customFilterOptions,
		spendType,
		dateFrom,
		dateTo,
		hierarchy,
	} = props;
	const { customFilters } = getCustomFilters(props);
	const yearFrom = getYearByDate(dateFrom);
	const yearTo = getYearByDate(dateTo);
	const yearString = yearFrom === yearTo ? yearFrom : `${yearFrom} - ${yearTo}`;
	const sourceableLabel =
		showNonSourceable === 'true' ? 'Non-sourceable' : 'Sourceable';
	const spendTypeLabel =
		spendType != null ? SPEND_TYPE_LABELS[spendType] : null;

	const customFiltersOccurances = customFilters.reduce((acc, filter) => {
		if (!acc[filter]) {
			const customFilterObj = customFilterOptions.find(
				item => item.name === filter
			);
			return {
				...acc,
				[filter]: {
					count: 1,
					label: customFilterObj ? customFilterObj.label : filter,
				},
			};
		}

		return {
			...acc,
			[filter]: { ...acc[filter], count: acc[filter].count + 1 },
		};
	}, {});

	return [
		sourceableLabel,
		...(spendTypeLabel ? [spendTypeLabel] : []),
		yearString,
		useNetSpend || showNegativeSpend ? 'Net spend' : 'Positive spend',
		...(hierarchy.length > 0 ? [hierarchy.slice(-1)[0]] : []),
		...Object.keys(customFiltersOccurances).map(
			name =>
				`${customFiltersOccurances[name].label} (${
					customFiltersOccurances[name].count
				})`
		),
	];
};

export const getRequestParams = props => {
	const {
		currency,
		currencyType,
		dateFrom,
		dateTo,
		hierarchy,
		showNonSourceable,
		customFiltersValues,
		customFilters,
		storedQueryId,
		spendType,
		selectedDimension,
		itemsOnPage,
		search,
		quickFilter,
		sorting,
		getTailSpendOnly,
		indirectTaxonomy,
		directTaxonomy,
	} = props;

	if (!selectedDimension) {
		return null;
	}
	const dimensionParam = Array(selectedDimension.level).fill(
		selectedDimension.dimensionName
	);
	const params = {
		harmonized: true,
		dimensions: dimensionParam,
		currency,
		currencyType: 'fixed',
		sortDirection: sorting.direction,
		dateFrom,
		dateTo,
		skip: 0,
		take: itemsOnPage,
		sortIndex: sorting.columnIndex,
		SearchTerm: search,
		quickSearchType: QUICK_FILTERS_MAP[quickFilter],
		ContextFilters: [],
		hierarchy,
		storedQueryId,
		getTailSpendOnly,
		indirectTaxonomyType: indirectTaxonomy,
		directTaxonomyType: directTaxonomy,
	};
	params.dateFrom = dateFrom;
	params.dateTo = dateTo;
	params.filters = ['sourceable'];
	params.filterValues = [showNonSourceable === 'false'];

	if (customFiltersValues.length > 0) {
		params.filters = [...params.filters, ...customFilters];
		params.filterValues = [...params.filterValues, ...customFiltersValues];
	}

	if (spendType != null) {
		params.filters = [...params.filters, 'spendType'];
		params.filterValues = [...params.filterValues, spendType];
	}

	if (selectedDimension.filter) {
		params.filters = [...(params.filters || []), selectedDimension.filter];
		params.filterValues = [
			...(params.filterValues || []),
			selectedDimension.filterValue,
		];
	}
	params.currency = currency;
	params.currencyType = currencyType;

	return params;
};
